import { useMemo, useState } from "react";
import { AlertTriangle, Search } from "lucide-react";

import { EmptyState } from "@/components/EmptyState";
import { Panel } from "@/components/Panel";
import type { UnidentifiedLead } from "@/lib/types";
import { usePayload } from "@/lib/usePayload";
import { formatDate } from "@/lib/format";

const COLUMN_PRIORITY = [
  ["Lead Name", ["Lead Name", "Full_Name", "Lead_Name", "Name"]],
  ["Email", ["Email"]],
  ["Company", ["Company"]],
  ["Owner", ["Owner"]],
  ["Lead Source", ["Lead Source", "Lead_Source", "Source"]],
  ["Received", ["Lead Received Date", "Lead_Received_Date", "Received", "Date"]],
] as const;

function pickField(lead: UnidentifiedLead, candidates: readonly string[]): string {
  for (const key of candidates) {
    const v = lead[key];
    if (v != null && v !== "") return String(v);
  }
  return "";
}

export function UnidentifiedLeads() {
  const { payload, isLoading } = usePayload();
  const [query, setQuery] = useState("");

  const leads = payload?.still_unidentified || [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l: any) =>
      Object.values(l).some((v) => v != null && String(v).toLowerCase().includes(q))
    );
  }, [leads, query]);

  if (!payload) return <EmptyState title="No data yet for this period" loading={isLoading} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Unidentified Leads Audit</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Leads requiring source classification — {payload.month}
        </p>
      </div>

      {leads.length > 0 && (
        <div className="rounded-card border border-red-200 bg-red-50/70 px-4 py-3 flex gap-3 items-start">
          <AlertTriangle className="h-4 w-4 mt-0.5 text-red-600 shrink-0" />
          <div className="text-sm">
            <span className="font-semibold text-red-700"><span className="numeric-value">{leads.length}</span> unidentified leads </span>
            <span className="text-slate-700">
              this month need source review. Proper attribution affects channel efficiency reporting accuracy.
            </span>
          </div>
        </div>
      )}

      <Panel
        title={
          <span className="flex items-center gap-1.5">
            <span>Unidentified Leads</span>
            <span className="numeric-value font-medium text-slate-500">({leads.length})</span>
          </span>
        }
        right={
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search leads..."
              className="pl-8 pr-3 py-1.5 text-sm rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-accent-blue/20 focus:border-accent-blue/50 bg-white"
            />
          </div>
        }
      >
        {filtered.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            {leads.length === 0 ? "No unidentified leads." : "No matches."}
          </div>
        ) : (
          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left label-xs border-b border-border">
                  {COLUMN_PRIORITY.map(([h]) => (
                    <th key={h} className="font-medium px-5 py-2.5 first:px-5 [&:not(:first-child):not(:last-child)]:px-3">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((lead: any, idx: number) => {
                  const cells = COLUMN_PRIORITY.map(([, keys]) => pickField(lead, keys));
                  const [name, email, company, owner, source, received] = cells;
                  return (
                    <tr key={idx} className="border-b border-border last:border-0 hover:bg-slate-50/60">
                      <td className="px-5 py-2.5 font-medium">{name || "—"}</td>
                      <td className="px-3 py-2.5">
                        {email ? (
                          <a className="text-accent-blue hover:underline" href={`mailto:${email}`}>
                            {email}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">{company || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-700">{owner || "—"}</td>
                      <td className="px-3 py-2.5">
                        {source ? (
                          <span className="pill bg-amber-50 border border-amber-200 text-amber-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                            {source}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-5 py-2.5 numeric-value text-slate-500">
                        {received ? formatDate(received) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
